import { wrappingInputRule } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import { ListItem, OrderedList } from '@tiptap/extension-list';
import { TextSelection } from '@tiptap/pm/state';
import {
  markdownPrefixToListKind,
  orderedListStartFromDigits,
  shouldLiftEmptyListItem,
} from './richCommentListShortcuts';

/**
 * Enter on an empty item leaves the list (or outdents a nested item) instead
 * of inserting another blank row.
 */
export const CommentListItem = ListItem.extend({
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        const { empty, $from } = this.editor.state.selection;
        if (
          shouldLiftEmptyListItem({
            selectionEmpty: empty,
            inListItem: this.editor.isActive(this.name),
            currentBlockEmpty: $from.parent.isTextblock && $from.parent.content.size === 0,
          })
        ) {
          if (this.editor.can().liftListItem(this.name)) {
            return this.editor.commands.liftListItem(this.name);
          }
          if (this.editor.isActive('orderedList')) {
            return this.editor.commands.toggleOrderedList();
          }
          if (this.editor.isActive('bulletList')) {
            return this.editor.commands.toggleBulletList();
          }
        }
        return this.editor.commands.splitListItem(this.name);
      },
      Tab: () => this.editor.commands.sinkListItem(this.name),
      'Shift-Tab': () => this.editor.commands.liftListItem(this.name),
    };
  },
});

/** Default TipTap rule requires `1. ` (trailing space). Also accept `1.` alone. */
export const CommentOrderedList = OrderedList.extend({
  addInputRules() {
    const rule = (find: RegExp) =>
      wrappingInputRule({
        find,
        type: this.type,
        getAttributes: (match) => ({ start: +match[1] }),
        joinPredicate: (match, node) => node.childCount + node.attrs.start === +match[1],
      });
    return [rule(/^(\d+)\.$/), rule(/^(\d+)\.\s$/)];
  },
});

export function commentListExtensions() {
  return [CommentListItem, CommentOrderedList];
}

/** Drop StarterKit's copies so the comment list nodes above are the only ones. */
export const commentListStarterKitOptions = {
  listItem: false,
  orderedList: false,
} as const;

function replaceTextblockWithList(
  editor: Editor,
  kind: 'ordered' | 'bullet',
  start = 1,
): boolean {
  const { state } = editor;
  const { empty, $from } = state.selection;
  if (!empty || !$from.parent.isTextblock) return false;
  const schema = state.schema;
  const paragraph = schema.nodes.paragraph.create();
  const item = schema.nodes.listItem.create(null, paragraph);
  const list =
    kind === 'ordered'
      ? schema.nodes.orderedList.create({ start }, item)
      : schema.nodes.bulletList.create(null, item);
  const from = $from.before();
  const to = $from.after();
  const tr = state.tr.replaceWith(from, to, list);
  const cursor = Math.min(from + 3, tr.doc.content.size);
  tr.setSelection(TextSelection.near(tr.doc.resolve(cursor), 1));
  editor.view.dispatch(tr.scrollIntoView());
  return true;
}

function tryStartOrderedListOnPeriod(editor: Editor, event: KeyboardEvent): boolean {
  if (event.key !== '.' || event.metaKey || event.ctrlKey || event.altKey || event.isComposing) return false;
  if (editor.isActive('codeBlock') || editor.isActive('code')) return false;
  if (editor.isActive('listItem')) return false;
  const { empty, $from } = editor.state.selection;
  if (!empty || !$from.parent.isTextblock) return false;
  if ($from.parentOffset !== $from.parent.content.size) return false;
  const start = orderedListStartFromDigits($from.parent.textContent);
  if (start == null) return false;
  event.preventDefault();
  return replaceTextblockWithList(editor, 'ordered', start);
}

/** `-`, `*`, or `+` at the start of a line, then Space, becomes a bullet list. */
function tryStartBulletListOnSpace(editor: Editor, event: KeyboardEvent): boolean {
  if (event.key !== ' ' || event.metaKey || event.ctrlKey || event.altKey || event.isComposing) return false;
  if (editor.isActive('codeBlock') || editor.isActive('code') || editor.isActive('listItem')) return false;
  const { empty, $from } = editor.state.selection;
  if (!empty || !$from.parent.isTextblock) return false;
  if ($from.parentOffset !== $from.parent.content.size) return false;
  if (markdownPrefixToListKind($from.parent.textContent) !== 'bullet') return false;
  event.preventDefault();
  return replaceTextblockWithList(editor, 'bullet');
}

function tryLiftEmptyListItemOnEnter(editor: Editor, event: KeyboardEvent): boolean {
  if (event.key !== 'Enter' || event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) {
    return false;
  }
  const { empty, $from } = editor.state.selection;
  if (
    !shouldLiftEmptyListItem({
      selectionEmpty: empty,
      inListItem: editor.isActive('listItem'),
      currentBlockEmpty: $from.parent.isTextblock && $from.parent.content.size === 0,
    })
  ) {
    return false;
  }
  event.preventDefault();
  if (editor.can().liftListItem('listItem')) {
    editor.commands.liftListItem('listItem');
    return true;
  }
  if (editor.isActive('orderedList')) {
    editor.commands.toggleOrderedList();
    return true;
  }
  if (editor.isActive('bulletList')) {
    editor.commands.toggleBulletList();
    return true;
  }
  return false;
}

/** Shared key handling for every TipTap comment (and board note) editor. */
/** Tab nests the item; Shift-Tab steps back one level. Same list type, next outline style. */
function tryIndentListItem(editor: Editor, event: KeyboardEvent): boolean {
  if (event.key !== 'Tab' || event.metaKey || event.ctrlKey || event.altKey) return false;
  if (!editor.isActive('listItem')) return false;
  event.preventDefault();
  if (event.shiftKey) {
    editor.commands.liftListItem('listItem');
    return true;
  }
  editor.commands.sinkListItem('listItem');
  return true;
}

export function handleCommentListKeyDown(editor: Editor, event: KeyboardEvent): boolean {
  if (tryIndentListItem(editor, event)) return true;
  if (tryStartOrderedListOnPeriod(editor, event)) return true;
  if (tryStartBulletListOnSpace(editor, event)) return true;
  if (tryLiftEmptyListItemOnEnter(editor, event)) return true;
  return false;
}
